/**
 * Utilitários de CPF (domínio puro, sem HTTP).
 *
 * - Centraliza regras de validação e normalização.
 * - Pode ser usado em qualquer módulo (register, profile, formulários, etc.).
 */

export type CpfValidationError =
  | "required"
  | "invalid_format"
  | "invalid_length"
  | "invalid_digits";

export interface CpfValidationResult {
  isValid: boolean;
  error?: CpfValidationError;
}

/**
 * Remove qualquer caractere não numérico de um CPF.
 * Ex.: "123.456.789-00" -> "12345678900"
 */
export function normalizeCpf(cpf: string): string {
  return cpf.replace(/\D/g, "");
}

/**
 * Valida CPF:
 * - Não vazio
 * - Apenas dígitos
 * - Tamanho 11
 * - Não aceita todos os dígitos iguais
 * - Valida dígitos verificadores
 */
export function validateCpf(cpf: string | null | undefined): CpfValidationResult {
  if (!cpf) {
    return { isValid: false, error: "required" };
  }

  const digits = normalizeCpf(cpf);

  if (!/^\d+$/.test(digits)) {
    return { isValid: false, error: "invalid_format" };
  }

  if (digits.length !== 11) {
    return { isValid: false, error: "invalid_length" };
  }

  if (/^(\d)\1+$/.test(digits)) {
    return { isValid: false, error: "invalid_digits" };
  }

  const calcCheckDigit = (base: string): number => {
    let sum = 0;
    for (let i = 0; i < base.length; i += 1) {
      sum += parseInt(base[i], 10) * (base.length + 1 - i);
    }
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  const firstNine = digits.substring(0, 9);
  const firstCheck = calcCheckDigit(firstNine);
  if (firstCheck !== parseInt(digits[9], 10)) {
    return { isValid: false, error: "invalid_digits" };
  }

  const firstTen = digits.substring(0, 10);
  const secondCheck = calcCheckDigit(firstTen);
  if (secondCheck !== parseInt(digits[10], 10)) {
    return { isValid: false, error: "invalid_digits" };
  }

  return { isValid: true };
}

